import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Badge,
  Box,
  Button,
  Divider,
  List,
  ListSubheader,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import Iconify from '@/components/Iconify';
import Scrollbar from '@/components/Scrollbar';
import MenuPopover from '@/components/MenuPopover';
import { useSnackbarMsg } from '@/hooks/useSnackbarMsg';
import IconNotificationAnimate from './IconNotificationAnimate';
import NotificationItem from './NotificationItem';
import { ROOTS_NOTIFICATIONS } from '../routes/paths';
import {
  getNotificationLimitedRequest,
  getNotificationsReadAllRequest,
} from '../api';
import { sortNotificationsByCreatedAtDesc } from '../utils';

export default function NotificationsPopover() {
  const queryClient = useQueryClient();
  const { errorMsg } = useSnackbarMsg();
  const { data, isLoading } = useQuery({
    queryKey: ['notifications', 'popover'],
    queryFn: () => getNotificationLimitedRequest(5),
    refetchInterval: 30000,
  });

  const notifications = useMemo(
    () => sortNotificationsByCreatedAtDesc(data?.notifications ?? []),
    [data?.notifications]
  );
  const totalUnseen = data?.totalUnseen ?? 0;
  const initialLoading = isLoading && !data;
  const unreadNotifications = notifications.filter(
    (notification) => !notification.seen
  );
  const readNotifications = notifications.filter(
    (notification) => notification.seen
  );

  const [open, setOpen] = useState<HTMLElement | null>(null);
  const navigate = useNavigate();

  const readAllMutation = useMutation({
    mutationFn: getNotificationsReadAllRequest,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications', 'popover'] });
      queryClient.invalidateQueries({ queryKey: ['notifications', 'list'] });
    },
    onError: (error) => {
      errorMsg(error, 'There was an error while marking notifications as read');
    },
  });

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setOpen(event.currentTarget);
  };

  const handleClose = useCallback(() => {
    setOpen(null);
  }, []);

  const handleViewAll = useCallback(() => {
    handleClose();
    navigate(ROOTS_NOTIFICATIONS);
  }, [handleClose, navigate]);

  const handleReadAll = useCallback(() => {
    if (!totalUnseen) return;

    readAllMutation.mutate();
  }, [readAllMutation, totalUnseen]);

  return initialLoading ? (
    <Skeleton variant="circular" width={40} height={40} />
  ) : (
    <>
      <IconNotificationAnimate
        count={totalUnseen}
        color={open ? 'primary' : 'default'}
        onClick={handleOpen}
        sx={{
          width: 40,
          height: 40,
          '&:hover': { backgroundColor: 'background.paper' },
        }}
      >
        <Badge badgeContent={totalUnseen} color="error">
          <Iconify icon="eva:bell-fill" width={25} />
        </Badge>
      </IconNotificationAnimate>

      <MenuPopover
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleClose}
        sx={{ width: 390, p: 0, mt: 1.5, ml: 0.75 }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={2}
          sx={{ py: 2, px: 2.5 }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1">Notifications</Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {totalUnseen
                ? `${totalUnseen} unread notification${
                    totalUnseen === 1 ? '' : 's'
                  }`
                : 'All caught up'}
            </Typography>
          </Box>

          {totalUnseen > 0 && (
            <LoadingButton
              size="small"
              loading={readAllMutation.isLoading}
              onClick={handleReadAll}
              sx={{ flexShrink: 0, fontWeight: 700 }}
            >
              Mark all read
            </LoadingButton>
          )}
        </Stack>

        <Divider sx={{ borderStyle: 'dashed' }} />

        <Scrollbar sx={{ maxHeight: { xs: 340, sm: 430 } }}>
          {notifications.length ? (
            <>
              {!!unreadNotifications.length && (
                <List
                  disablePadding
                  subheader={
                    <ListSubheader
                      disableSticky
                      sx={{
                        py: 1,
                        px: 2.5,
                        typography: 'overline',
                        letterSpacing: 0,
                        color: 'text.disabled',
                      }}
                    >
                      New
                    </ListSubheader>
                  }
                >
                  {unreadNotifications.map((notification) => (
                    <NotificationItem
                      key={notification.id}
                      notificationObject={notification}
                      popover
                    />
                  ))}
                </List>
              )}

              {!!readNotifications.length && (
                <List
                  disablePadding
                  subheader={
                    <ListSubheader
                      disableSticky
                      sx={{
                        py: 1,
                        px: 2.5,
                        typography: 'overline',
                        letterSpacing: 0,
                        color: 'text.disabled',
                      }}
                    >
                      Before that
                    </ListSubheader>
                  }
                >
                  {readNotifications.map((notification) => (
                    <NotificationItem
                      key={notification.id}
                      notificationObject={notification}
                      popover
                    />
                  ))}
                </List>
              )}
            </>
          ) : (
            <Stack
              spacing={1}
              alignItems="center"
              sx={{ py: 5, px: 3, textAlign: 'center' }}
            >
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'text.secondary',
                  bgcolor: 'background.neutral',
                }}
              >
                <Iconify icon="eva:bell-outline" width={22} />
              </Box>
              <Typography variant="subtitle2">No notifications yet</Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                New activity will appear here.
              </Typography>
            </Stack>
          )}
        </Scrollbar>

        <Divider sx={{ borderStyle: 'dashed' }} />

        <Box sx={{ p: 1 }}>
          <Button fullWidth disableRipple onClick={handleViewAll}>
            View all
          </Button>
        </Box>
      </MenuPopover>
    </>
  );
}
